import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Appearance, Intent, IntentType, Size } from '@Components/designConstants';
import { useSetError } from '../../redux/modals';
import { awaitJSON, ClientToolsLambdaFetch } from '../../utils/fetch-utils';
import {
  BLANK_CLIENT_TIME_LOG,
  buildInitialReport,
  ClientTimeFieldKey,
  computeGrandTotal,
  computeLineItemTotal,
  CpsApprovalStatus,
  CpsClientSearchResult,
  CpsCurrentUserResponse,
  CpsReportForm,
  formatSurveyPeriod,
  hydrateReportFromLastReport,
  INSTRUCTIONS_LINK,
  InternalTimeFieldKey,
  isReportAlreadySubmitted,
  isReportEditableForPeriod,
  removeStaleRelationships,
  SURVEY_DESCRIPTION_1,
  SURVEY_DESCRIPTION_2,
  transformReportToInput,
  validateReport
} from './cps-utils';
import { SaveIndicator } from './SaveIndicator';
import { SurveyInternalTime } from './SurveyInternalTime';
import { SurveyLineItem } from './SurveyLineItem';
import { SurveyTableHeaders } from './SurveyTableHeaders';
import { TimeInput } from './TimeInput';

interface SurveyTabProps {
  cpsUser: CpsCurrentUserResponse;
  surveyDate: string;
  surveyClosed: boolean;
}

interface ModalState {
  show: boolean;
  intent: IntentType;
  title: string;
  messages: string[];
  showConfirm?: boolean;
}

const INITIAL_MODAL: ModalState = {
  show: false,
  intent: Intent.INFO,
  title: '',
  messages: []
};

const SurveyTab = ({ cpsUser, surveyDate, surveyClosed }: SurveyTabProps) => {
  const setError = useSetError();
  const [report, setReport] = useState<CpsReportForm>(() => {
    if (isReportEditableForPeriod(cpsUser.lastReport, surveyDate)) {
      const hydrated = hydrateReportFromLastReport(cpsUser.lastReport!);
      return removeStaleRelationships(hydrated, cpsUser);
    }
    return buildInitialReport(cpsUser);
  });
  const [surveySubmitted, setSurveySubmitted] = useState(
    isReportAlreadySubmitted(cpsUser.lastReport, surveyDate)
  );
  const [modal, setModal] = useState<ModalState>(INITIAL_MODAL);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const draftTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(
    () => () => {
      clearTimeout(draftTimeoutRef.current);
      clearTimeout(savedTimeoutRef.current);
    },
    []
  );

  const saveDraft = useCallback(
    (currentReport: CpsReportForm) => {
      clearTimeout(draftTimeoutRef.current);
      draftTimeoutRef.current = setTimeout(async () => {
        try {
          setDraftSaving(true);
          setDraftSaved(false);
          const payload = transformReportToInput(currentReport, surveyDate);
          if (currentReport.id) {
            payload.reportId = currentReport.id;
          }
          const res = await ClientToolsLambdaFetch('/cps/saveDraftReport', {
            method: 'POST',
            body: payload
          });
          const reportId = await awaitJSON<string>(res);
          if (reportId && reportId !== currentReport.id) {
            setReport(prev => ({ ...prev, id: reportId, approvalStatus: CpsApprovalStatus.DRAFT }));
          }
          setDraftSaved(true);
          clearTimeout(savedTimeoutRef.current);
          savedTimeoutRef.current = setTimeout(() => setDraftSaved(false), 3000);
        } catch (e) {
          setError({ message: `Draft save failed: ${e.message}`, reportError: e });
        } finally {
          setDraftSaving(false);
        }
      }, 2000);
    },
    [surveyDate, setError]
  );

  const updateReportAndSaveDraft = useCallback(
    (newReport: CpsReportForm) => {
      setReport(newReport);
      saveDraft(newReport);
    },
    [saveDraft]
  );

  const handleClientChange = useCallback(
    (index: number, client: CpsClientSearchResult | null) => {
      const newLogs = [...report.clientTimeLogs];
      newLogs[index] = { ...newLogs[index], client, relationship: null };
      const newReport = { ...report, clientTimeLogs: newLogs };
      updateReportAndSaveDraft(newReport);
    },
    [report, updateReportAndSaveDraft]
  );

  const handleRelationshipChange = useCallback(
    (index: number, relationshipId: string) => {
      const newLogs = [...report.clientTimeLogs];
      const { client } = newLogs[index];
      const rel = client?.relationships?.find(r => r.id === relationshipId);
      newLogs[index] = {
        ...newLogs[index],
        relationship: rel ? { id: rel.id, name: rel.name } : null
      };
      const newReport = { ...report, clientTimeLogs: newLogs };
      updateReportAndSaveDraft(newReport);
    },
    [report, updateReportAndSaveDraft]
  );

  const handleTimeChange = useCallback(
    (index: number, field: ClientTimeFieldKey, value: number) => {
      const newLogs = [...report.clientTimeLogs];
      newLogs[index] = { ...newLogs[index], [field]: value };
      newLogs[index].totalHours = computeLineItemTotal(newLogs[index]);
      const totalHoursWorked = computeGrandTotal(newLogs, report.internalTimeLogs);
      const newReport = { ...report, clientTimeLogs: newLogs, totalHoursWorked };
      updateReportAndSaveDraft(newReport);
    },
    [report, updateReportAndSaveDraft]
  );

  const handleInternalTimeChange = useCallback(
    (key: InternalTimeFieldKey, value: number) => {
      const newInternalTimeLogs = { ...report.internalTimeLogs, [key]: value };
      const totalHoursWorked = computeGrandTotal(report.clientTimeLogs, newInternalTimeLogs);
      const newReport = { ...report, internalTimeLogs: newInternalTimeLogs, totalHoursWorked };
      updateReportAndSaveDraft(newReport);
    },
    [report, updateReportAndSaveDraft]
  );

  const addClient = useCallback(() => {
    const newLogs = [...report.clientTimeLogs, { ...BLANK_CLIENT_TIME_LOG }];
    const newReport = { ...report, clientTimeLogs: newLogs };
    updateReportAndSaveDraft(newReport);
  }, [report, updateReportAndSaveDraft]);

  const deleteLineItem = useCallback(
    (index: number) => {
      const newLogs = [...report.clientTimeLogs];
      newLogs.splice(index, 1);
      const totalHoursWorked = computeGrandTotal(newLogs, report.internalTimeLogs);
      const newReport = { ...report, clientTimeLogs: newLogs, totalHoursWorked };
      updateReportAndSaveDraft(newReport);
    },
    [report, updateReportAndSaveDraft]
  );

  const submitReport = useCallback(
    async (bypassWarnings = false) => {
      clearTimeout(draftTimeoutRef.current);
      const input = transformReportToInput(report, surveyDate);
      const { errors, warnings } = validateReport(input);

      if (errors.length) {
        setModal({
          show: true,
          intent: Intent.ERROR,
          title: 'Validation Error',
          messages: errors
        });
        return;
      }
      if (warnings.length && !bypassWarnings) {
        setModal({
          show: true,
          intent: Intent.WARNING,
          title: 'Warning',
          messages: warnings,
          showConfirm: true
        });
        return;
      }

      try {
        const isUpdate =
          report.approvalStatus === CpsApprovalStatus.REJECTED ||
          report.approvalStatus === CpsApprovalStatus.DRAFT;
        const endpoint = isUpdate ? '/cps/updateReport' : '/cps/saveReport';
        if (isUpdate && report.id) {
          input.reportId = report.id;
        }
        await ClientToolsLambdaFetch(endpoint, {
          method: 'POST',
          body: input
        });
        setSurveySubmitted(true);
        setModal({
          show: true,
          intent: Intent.SUCCESS,
          title: 'Success',
          messages: ['Survey submitted successfully']
        });
      } catch (e) {
        setError({ message: `Failed to submit survey: ${e.message}`, reportError: e });
      }
    },
    [report, surveyDate, setError]
  );

  const hideModal = useCallback(() => setModal(INITIAL_MODAL), []);

  if (surveyClosed) {
    return (
      <div className='cps-survey-closed'>
        <p>The submission window for this month has closed. Please check back next month.</p>
      </div>
    );
  }

  if (surveySubmitted) {
    return (
      <div className='cps-survey-submitted'>
        <p>
          Thank you, you have already submitted a survey for the current time period. Please return
          next month.
        </p>
      </div>
    );
  }

  const getAlertVariant = () => {
    if (modal.intent === 'error') return Intent.ERROR;
    if (modal.intent === 'warning') return Intent.WARNING;
    return Intent.SUCCESS;
  };
  const alertVariant = getAlertVariant();

  return (
    <div className='cps-survey-tab'>
      <div className='cps-survey-header'>
        <div className='cps-survey-period'>
          <strong>Survey Period:</strong> {formatSurveyPeriod(surveyDate)}
        </div>
        <div className='cps-survey-user-info'>
          <span>{cpsUser.name}</span>
          <span className='cps-survey-user-detail'>{cpsUser.channel}</span>
        </div>
      </div>

      <div className='cps-survey-description'>
        <p>
          {SURVEY_DESCRIPTION_1}{' '}
          <a href={INSTRUCTIONS_LINK} target='_blank' rel='noreferrer'>
            here
          </a>
          .
        </p>
        <p>{SURVEY_DESCRIPTION_2}</p>
      </div>

      <SurveyTableHeaders />
      {report.clientTimeLogs.map((log, i) => (
        <SurveyLineItem
          key={`line-${i}-${log.client?.id ?? 'empty'}`}
          index={i}
          log={log}
          onClientChange={handleClientChange}
          onRelationshipChange={handleRelationshipChange}
          onTimeChange={handleTimeChange}
          onDelete={deleteLineItem}
        />
      ))}
      <div className='cps-add-client-row'>
        <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={addClient}>
          + Add Client
        </Button>
      </div>

      <hr className='cps-divider' />

      <SurveyInternalTime
        internalTimeLogs={report.internalTimeLogs}
        onChange={handleInternalTimeChange}
      />

      <div className='cps-grand-total-row'>
        <span className='cps-grand-total-label'>Total</span>
        <TimeInput value={report.totalHoursWorked} readOnly isTotal />
      </div>

      <div className='cps-submit-row'>
        <Button size={Size.LG} onClick={() => submitReport()}>
          Submit
        </Button>
      </div>

      <SaveIndicator saving={draftSaving} saved={draftSaved} />

      <AlertDialog
        show={modal.show}
        onHide={hideModal}
        intent={alertVariant}
        title={modal.title}
        body={
          <ul>
            {modal.messages.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        }
        primaryAction={{
          onClick: modal.showConfirm ? () => submitReport(true) : hideModal,
          label: modal.showConfirm ? 'Submit Anyway' : undefined
        }}
        secondaryAction={{ onClick: modal.showConfirm ? hideModal : undefined }}
      />
    </div>
  );
};

export { SurveyTab };
